import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Briefcase, Users, MessageSquare, Sparkles, Phone } from "lucide-react";
import api from "../lib/api";
import { getRecents } from "../lib/recents";

const KIND_META = {
  customer: { icon: Users, label: "Customer", route: (id) => `/customers/${id}` },
  jobcard: { icon: Briefcase, label: "Job card", route: (id) => `/jobcards/${id}` },
  ticket: { icon: MessageSquare, label: "Ticket", route: (id) => `/tickets/${id}` },
  lead: { icon: Sparkles, label: "Lead", route: () => `/leads` },
  task: { icon: Sparkles, label: "Task", route: () => `/tasks` },
  call: { icon: Phone, label: "Call log", route: () => `/calls` },
};

/**
 * Global Cmd/Ctrl+K command palette.
 *
 * - Triggered by Cmd+K (mac) / Ctrl+K (Windows/Linux).
 * - Pulls customers, jobcards, tickets, leads (lazy on first open).
 * - Shows recents on empty input, then live-filters across all entity
 *   types as the user types.
 *
 * The palette is rendered globally (mounted once in Layout) so it works
 * on every authenticated page without per-page boilerplate.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ customers: [], jobcards: [], tickets: [], leads: [] });
  const [loaded, setLoaded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recents, setRecents] = useState(getRecents());
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd/Ctrl+K toggles the palette. Esc closes it.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Refresh recents whenever the palette is opened.
  useEffect(() => {
    if (open) {
      setRecents(getRecents());
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Lazy-load data on first open.
  useEffect(() => {
    if (!open || loaded) return;
    Promise.all([
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/jobcards").catch(() => ({ data: [] })),
      api.get("/tickets").catch(() => ({ data: [] })),
      api.get("/leads").catch(() => ({ data: [] })),
    ]).then(([c, j, t, l]) => {
      setData({
        customers: c.data || [],
        jobcards: j.data || [],
        tickets: t.data || [],
        leads: l.data || [],
      });
      setLoaded(true);
    });
  }, [open, loaded]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty query → show recents (max 8).
      return recents.map((r) => ({ ...r }));
    }
    const out = [];
    const match = (s) => (s || "").toLowerCase().includes(q);
    for (const c of data.customers) {
      if (
        match(c.name) ||
        match(c.email) ||
        match(c.phone) ||
        match(c.external_ref) ||
        match(c.id)
      ) {
        out.push({
          kind: "customer",
          id: c.id,
          label: c.name,
          sub: c.external_ref || c.email || c.phone || "",
        });
        if (out.length >= 30) break;
      }
    }
    for (const j of data.jobcards) {
      if (out.length >= 30) break;
      if (match(j.title) || match(j.short_id) || match(j.id)) {
        out.push({
          kind: "jobcard",
          id: j.id,
          label: j.title || j.short_id || j.id?.slice(0, 8),
          sub: j.short_id || "",
        });
      }
    }
    for (const t of data.tickets) {
      if (out.length >= 30) break;
      if (match(t.subject) || match(t.short_id) || match(t.id)) {
        out.push({
          kind: "ticket",
          id: t.id,
          label: t.subject || t.short_id || t.id?.slice(0, 8),
          sub: t.short_id || "",
        });
      }
    }
    for (const l of data.leads) {
      if (out.length >= 30) break;
      if (match(l.title) || match(l.id)) {
        out.push({
          kind: "lead",
          id: l.id,
          label: l.title || l.id?.slice(0, 8),
          sub: "",
        });
      }
    }
    return out;
  }, [query, data, recents]);

  // Keep activeIdx in range when results change.
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(0, results.length - 1)));
  }, [results.length]);

  const go = (item) => {
    if (!item) return;
    const meta = KIND_META[item.kind];
    if (!meta) return;
    navigate(meta.route(item.id));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIdx]);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 hv-safe-x"
      onClick={(e) => e.target === e.currentTarget && setOpen(false)}
      data-testid="command-palette"
    >
      <div className="w-full max-w-xl bg-[#0f1216] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
        <div className="relative flex items-center border-b border-white/5">
          <Search className="absolute left-4 w-4 h-4 text-[#647087] pointer-events-none" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search customers, job cards, tickets, leads… (⌘K)"
            className="w-full bg-transparent text-sm pl-10 pr-12 py-3.5 text-[#dde3ec] placeholder-[#647087] focus:outline-none"
            data-testid="command-palette-input"
          />
          <button
            onClick={() => setOpen(false)}
            className="absolute right-3 text-[#647087] hover:text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="command-palette-results">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-[#647087]">
              {query
                ? "No matches."
                : "Start typing or pick from recents — ⌘K from anywhere."}
            </div>
          ) : (
            <>
              {!query && recents.length > 0 && (
                <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#647087]">
                  Recent
                </div>
              )}
              {results.map((r, idx) => {
                const meta = KIND_META[r.kind];
                if (!meta) return null;
                const Icon = meta.icon;
                const active = idx === activeIdx;
                return (
                  <button
                    key={`${r.kind}-${r.id}`}
                    type="button"
                    onClick={() => go(r)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-white/5 transition ${
                      active ? "bg-[#1fb6c1]/10" : "hover:bg-white/[0.03]"
                    }`}
                    data-testid={`command-palette-row-${idx}`}
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 ${active ? "text-[#1fb6c1]" : "text-[#647087]"}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm truncate ${active ? "text-white" : "text-[#dde3ec]"}`}>
                        {r.label}
                      </div>
                      <div className="text-[10px] text-[#647087] uppercase tracking-wider flex gap-2">
                        <span>{meta.label}</span>
                        {r.sub && <span className="font-mono normal-case tracking-normal">· {r.sub}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
        <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between text-[10px] text-[#647087] uppercase tracking-wider">
          <span>↑↓ navigate · ↵ open · esc close</span>
          <span>⌘K</span>
        </div>
      </div>
    </div>
  );
}
